import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Building2, Clock, Wallet, ShieldCheck, FileText, Server, CheckCircle2, XCircle, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { sectorStyle } from "@/lib/sectors";
import { feeLabel, slaLabel } from "@/components/ServiceRow";

const Fact = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-xl border bg-card p-3 shadow-sm">
    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><Icon size={13} /> {label}</p>
    <p className="mt-1 text-sm font-semibold" data-testid={testId}>{value}</p>
  </div>
);

export default function ServiceDetailPage() {
  const { id } = useParams();
  const { t, catalog, svcName, secName, lang, me, refreshMe } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const s = catalog?.services.find((x) => x.service_id === id);
  if (!catalog) return <div className="m-4 h-64 animate-pulse rounded-2xl bg-muted" />;
  if (!s) return <p className="p-6 text-center">Service not found</p>;
  const st = sectorStyle(s.sector_id);
  const Icon = st.icon;
  const eligible = me?.user && s.eligible_personas.includes(me.user.persona);
  const steps = catalog.workflow_steps;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/requests", { service_id: s.service_id });
      sessionStorage.setItem(`q1_mfa_${data.ref}`, data.sandbox_mfa_code);
      refreshMe();
      nav(`/requests/${data.ref}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 px-4 pt-4" data-testid="service-detail-page">
      <button onClick={() => nav(-1)} className="inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-muted-foreground" data-testid="service-back-btn"><ArrowLeft size={16} className="q1-flip" /> {t("back")}</button>
      <div className="flex items-start gap-3">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow" style={{ background: st.color }}><Icon size={26} /></span>
        <div>
          <Link to={`/services?sector=${s.sector_id}`} className="text-xs font-semibold" style={{ color: st.color }}>{secName(s)} · {s.service_id}</Link>
          <h1 className="text-xl font-bold leading-snug" data-testid="service-detail-name">{svcName(s)}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground" dir={lang === "ar" ? "ltr" : "rtl"}>{lang === "ar" ? s.name_en : s.name_ar}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Fact icon={Wallet} label={t("fee")} value={feeLabel(s.fee_qar, t)} testId="service-detail-fee" />
        <Fact icon={Clock} label={t("sla")} value={slaLabel(s.sla_days, t)} testId="service-detail-sla" />
        <Fact icon={Building2} label={t("ministry")} value={lang === "ar" ? s.ministry_ar : s.ministry_en} />
        <Fact icon={ShieldCheck} label={t("mfa")} value={s.mfa_level} />
        <Fact icon={Server} label={t("backend")} value={`${s.backend_system} (simulated)`} />
        <Fact icon={FileText} label={t("channel")} value={s.channel} />
      </div>
      <div className="rounded-2xl border bg-card p-4 shadow-sm">
        <h2 className="mb-2 text-sm font-bold">{t("documents")}</h2>
        <ul className="space-y-1.5 text-sm">{s.documents_required.map((d) => <li key={d} className="flex items-center gap-2"><FileText size={14} className="text-muted-foreground" /> {d}</li>)}</ul>
      </div>
      <div className={`flex items-start gap-3 rounded-2xl border p-4 ${eligible ? "border-[#BADBCE] bg-[#D1E7DD] text-[#0F5132]" : "border-[#F5C2C7] bg-[#F8D7DA] text-[#842029]"}`} data-testid="service-eligibility-status">
        {eligible ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
        <div className="text-sm">
          <p className="font-bold">{eligible ? t("eligible") : t("notEligible")} ({me?.user?.persona})</p>
          <p className="mt-0.5 text-xs">{t("eligibility")}: {s.eligible_personas.join(" · ")}</p>
        </div>
      </div>
      <div className="rounded-2xl border bg-card p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-bold">{t("workflowSteps")}</h2>
        <ol className="space-y-2">
          {steps.map((w) => (
            <li key={w.step} className={`flex items-center gap-2.5 text-xs ${w.step === 4 && s.fee_qar === 0 ? "text-muted-foreground line-through" : ""}`}>
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--maroon-soft))] text-[11px] font-bold text-primary">{w.step}</span>
              {lang === "ar" ? w.name_ar : w.name_en} <span className="text-muted-foreground">· {w.system}</span>
            </li>
          ))}
        </ol>
      </div>
      {!eligible && <p className="text-center text-xs font-semibold text-[#842029] dark:text-red-400" data-testid="service-start-disabled-note">✕ {t("notEligible")} — {t("switchPersona")}: {s.eligible_personas.join(", ")}</p>}
      <button data-testid="service-start-request-btn" onClick={start} disabled={busy || !eligible}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-base font-bold text-primary-foreground shadow-lg transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : null} {t("startRequest")} <ArrowRight size={18} className="q1-flip" />
      </button>
    </div>
  );
}
